import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
    fetchThresholds, saveThresholds, resetThresholds,
    fetchAvailableStations, addStation, renameStation, removeStation,
    startImport, listImports, getImport, fetchFarm,
} from "@/lib/api";
import {
    Save, RotateCcw, Radio, Plus, Trash2, Pencil, Check, X, Download,
    Sliders, Snowflake, RefreshCcw, CalendarDays, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const FIELDS = {
    fdi: [
        { key: "green", label: "Low ≤" },
        { key: "yellow", label: "Moderate ≤" },
        { key: "orange", label: "High ≤" },
        { key: "red", label: "Extreme ≥" },
    ],
    delta_t: [
        { key: "green_low", label: "Green low ≥" },
        { key: "green_high", label: "Green high ≤" },
        { key: "yellow", label: "Yellow ≤" },
        { key: "orange", label: "Red ≥" },
    ],
    frost: [
        { key: "green", label: "Safe above" },
        { key: "yellow", label: "Caution below" },
        { key: "orange", label: "Risk below" },
        { key: "red", label: "Severe below" },
    ],
    rain_24h: [
        { key: "light", label: "Light ≥" },
        { key: "moderate", label: "Moderate ≥" },
        { key: "heavy", label: "Heavy ≥" },
    ],
};

const TITLES = {
    fdi: "Fire Danger Index thresholds",
    delta_t: "Delta T thresholds (°C)",
    frost: "Frost thresholds (°C, forecast minimum)",
    rain_24h: "Rain 24h categorisation (mm)",
};

export default function SettingsPage() {
    return (
        <div className="space-y-6" data-testid="settings-page">
            <div>
                <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Settings</h1>
                <p className="text-sm text-slate-500 mt-1">Manage stations, thresholds and historical imports</p>
            </div>

            <Tabs defaultValue="thresholds">
                <TabsList className="bg-white border border-slate-200 p-1 flex-wrap h-auto">
                    <TabsTrigger value="thresholds" data-testid="settings-tab-thresholds"><Sliders className="h-3.5 w-3.5 mr-1.5" />Thresholds</TabsTrigger>
                    <TabsTrigger value="stations" data-testid="settings-tab-stations"><Radio className="h-3.5 w-3.5 mr-1.5" />Weather Stations</TabsTrigger>
                    <TabsTrigger value="historical" data-testid="settings-tab-historical"><Download className="h-3.5 w-3.5 mr-1.5" />Historical Data</TabsTrigger>
                </TabsList>

                <TabsContent value="thresholds" className="mt-5">
                    <ThresholdsPanel />
                </TabsContent>
                <TabsContent value="stations" className="mt-5">
                    <StationsPanel />
                </TabsContent>
                <TabsContent value="historical" className="mt-5">
                    <HistoricalPanel />
                </TabsContent>
            </Tabs>
        </div>
    );
}

// ---------- Thresholds panel -----------------------------------------------
function ThresholdsPanel() {
    const { data } = useQuery({ queryKey: ["thresholds"], queryFn: fetchThresholds });
    const qc = useQueryClient();
    const [local, setLocal] = useState(null);
    useEffect(() => { if (data) setLocal(data); }, [data]);

    const save = useMutation({ mutationFn: saveThresholds,
        onSuccess: () => { qc.invalidateQueries(); toast.success("Thresholds updated"); } });
    const reset = useMutation({ mutationFn: resetThresholds,
        onSuccess: (d) => { setLocal(d); qc.invalidateQueries(); toast.success("Thresholds reset to defaults"); } });

    if (!local) return <div className="h-40 bg-white rounded-2xl border animate-pulse" />;

    const update = (group, key, value) =>
        setLocal((l) => ({ ...l, [group]: { ...l[group], [key]: Number(value) } }));

    return (
        <div className="space-y-4">
            <div className="flex justify-end gap-2 flex-wrap">
                <button onClick={() => reset.mutate()} data-testid="settings-reset"
                    className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-semibold rounded-lg border border-slate-200 bg-white hover:bg-slate-50">
                    <RotateCcw className="h-4 w-4" /> Reset defaults
                </button>
                <button onClick={() => save.mutate(local)} disabled={save.isPending} data-testid="settings-save"
                    className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-semibold rounded-lg bg-[var(--fw-accent)] hover:bg-[var(--fw-accent-hover)] text-white disabled:opacity-60">
                    <Save className="h-4 w-4" /> {save.isPending ? "Saving…" : "Save changes"}
                </button>
            </div>

            <div className="fw-card p-5">
                <div className="flex items-center gap-3 mb-1">
                    <Snowflake className="h-5 w-5 text-sky-500" />
                    <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>Frost detection threshold</h2>
                </div>
                <p className="text-xs text-slate-500 mb-4">Any period below this air temperature is counted as frost minutes. Default 2.2°C.</p>
                <div className="flex items-center gap-3">
                    <input
                        type="number" step="0.1"
                        value={local.frost_threshold_c ?? 2.2}
                        onChange={(e) => setLocal((l) => ({ ...l, frost_threshold_c: Number(e.target.value) }))}
                        data-testid="threshold-frost-c"
                        className="w-32 rounded-lg border border-slate-200 px-3 py-2 fw-metric font-semibold text-lg focus:outline-none focus:ring-2 focus:ring-[var(--fw-accent)]"
                    />
                    <span className="text-slate-500">°C</span>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(FIELDS).map(([group, fields]) => (
                    <div key={group} className="fw-card p-5">
                        <h2 className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: "Outfit" }}>{TITLES[group]}</h2>
                        <p className="text-xs text-slate-500 mb-4">Applied everywhere on the dashboard</p>
                        <div className="grid grid-cols-2 gap-3">
                            {fields.map((f) => (
                                <label key={f.key} className="block">
                                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{f.label}</span>
                                    <input type="number" step="0.1"
                                        value={local[group][f.key] ?? 0}
                                        onChange={(e) => update(group, f.key, e.target.value)}
                                        data-testid={`threshold-${group}-${f.key}`}
                                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 fw-metric font-semibold focus:outline-none focus:ring-2 focus:ring-[var(--fw-accent)]" />
                                </label>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ---------- Stations management panel --------------------------------------
function StationsPanel() {
    const { data: available, refetch } = useQuery({
        queryKey: ["available-stations"], queryFn: fetchAvailableStations,
    });
    const qc = useQueryClient();
    const [addOpen, setAddOpen] = useState(null);
    const [renaming, setRenaming] = useState(null);
    const [renameValue, setRenameValue] = useState("");

    const addMut = useMutation({
        mutationFn: addStation,
        onSuccess: (s) => {
            toast.success(`Added ${s.name}`);
            setAddOpen(null);
            qc.invalidateQueries();
            refetch();
        },
        onError: (e) => toast.error(e?.response?.data?.detail || "Failed to add station"),
    });
    const renameMut = useMutation({
        mutationFn: ({ id, name }) => renameStation(id, name),
        onSuccess: () => { toast.success("Station renamed"); setRenaming(null); qc.invalidateQueries(); refetch(); },
    });
    const removeMut = useMutation({
        mutationFn: removeStation,
        onSuccess: () => { toast.success("Station removed"); qc.invalidateQueries(); refetch(); },
    });

    if (!available) return <div className="h-40 bg-white rounded-2xl border animate-pulse" />;

    const added = available.stations.filter((s) => s.already_added);
    const notAdded = available.stations.filter((s) => !s.already_added);

    return (
        <div className="space-y-5">
            {/* Added stations */}
            <div className="fw-card p-5">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>Connected stations</h2>
                        <p className="text-xs text-slate-500">Data flows from Davis WeatherLink account <span className="fw-metric">{available.account}</span></p>
                    </div>
                    <RefreshCcw className="h-4 w-4 text-slate-400 cursor-pointer" onClick={() => refetch()} />
                </div>
                <div className="divide-y divide-slate-100">
                    {added.map((s) => (
                        <div key={s.weatherlink_id} className="py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`connected-${s.weatherlink_id}`}>
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="h-9 w-9 rounded-lg bg-emerald-50 border border-emerald-200 grid place-items-center">
                                    <Radio className="h-4 w-4 text-emerald-600" />
                                </div>
                                <div className="min-w-0">
                                    {renaming === s.weatherlink_id ? (
                                        <div className="flex items-center gap-1.5">
                                            <input
                                                autoFocus value={renameValue}
                                                onChange={(e) => setRenameValue(e.target.value)}
                                                className="rounded-md border border-slate-200 px-2 py-1 text-sm"
                                                data-testid={`rename-input-${s.weatherlink_id}`}
                                            />
                                            <button onClick={() => renameMut.mutate({ id: _slugify(s.name), name: renameValue })}
                                                className="p-1 text-emerald-600" data-testid={`rename-save-${s.weatherlink_id}`}>
                                                <Check className="h-4 w-4" />
                                            </button>
                                            <button onClick={() => setRenaming(null)} className="p-1 text-slate-400">
                                                <X className="h-4 w-4" />
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="font-semibold text-slate-900 truncate">{s.name}</div>
                                            <div className="text-[11px] text-slate-500 fw-metric">{s.weatherlink_id} · {tierLabel(s.tier)}</div>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <TierBadge tier={s.tier} />
                                <button onClick={() => { setRenaming(s.weatherlink_id); setRenameValue(s.name); }}
                                    className="p-2 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                                    data-testid={`rename-btn-${s.weatherlink_id}`}>
                                    <Pencil className="h-4 w-4" />
                                </button>
                                <button onClick={() => { if (window.confirm(`Remove ${s.name}?`)) removeMut.mutate(_slugify(s.name)); }}
                                    className="p-2 rounded-md text-red-500 hover:text-red-700 hover:bg-red-50"
                                    data-testid={`remove-btn-${s.weatherlink_id}`}>
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    ))}
                    {added.length === 0 && <div className="py-6 text-sm text-slate-500 text-center">No stations connected yet.</div>}
                </div>
            </div>

            {/* Available to add */}
            <div className="fw-card p-5">
                <div className="mb-3">
                    <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>Available on your account</h2>
                    <p className="text-xs text-slate-500">Select any station returned by <span className="fw-metric">GET /stations</span> to add it to Malabar Weather.</p>
                </div>
                <div className="space-y-2">
                    {notAdded.map((s) => (
                        <div key={s.weatherlink_id} className="flex items-center justify-between p-3 rounded-xl border border-slate-200"
                             data-testid={`available-${s.weatherlink_id}`}>
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="h-9 w-9 rounded-lg bg-slate-50 border border-slate-200 grid place-items-center">
                                    <Radio className="h-4 w-4 text-slate-400" />
                                </div>
                                <div className="min-w-0">
                                    <div className="font-semibold text-slate-900">{s.name}</div>
                                    <div className="text-[11px] text-slate-500 fw-metric">{s.weatherlink_id} · {s.lat.toFixed(3)}, {s.lon.toFixed(3)}</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <TierBadge tier={s.tier} />
                                <button onClick={() => setAddOpen(s)}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-[var(--fw-accent)] hover:bg-[var(--fw-accent-hover)] text-white"
                                    data-testid={`add-btn-${s.weatherlink_id}`}>
                                    <Plus className="h-3.5 w-3.5" /> Add
                                </button>
                            </div>
                        </div>
                    ))}
                    {notAdded.length === 0 && <div className="py-6 text-sm text-slate-500 text-center">All stations on this account have been added.</div>}
                </div>
            </div>

            <AddStationDialog open={!!addOpen} station={addOpen} onClose={() => setAddOpen(null)}
                onConfirm={(name) => addMut.mutate({
                    weatherlink_id: addOpen.weatherlink_id, display_name: name,
                    lat: addOpen.lat, lon: addOpen.lon,
                })}
                busy={addMut.isPending} />
        </div>
    );
}

function AddStationDialog({ open, station, onClose, onConfirm, busy }) {
    const [name, setName] = useState("");
    useEffect(() => { setName(station?.name || ""); }, [station]);
    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-md" data-testid="add-station-dialog">
                <DialogHeader>
                    <DialogTitle style={{ fontFamily: "Outfit" }}>Add {station?.name}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 mt-1">
                    <label className="block">
                        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Display name</span>
                        <input value={name} onChange={(e) => setName(e.target.value)} autoFocus
                            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 font-semibold focus:outline-none focus:ring-2 focus:ring-[var(--fw-accent)]"
                            data-testid="add-station-name" />
                    </label>
                    <div className="text-xs text-slate-500 fw-metric">
                        WeatherLink ID: {station?.weatherlink_id} · Tier: {tierLabel(station?.tier)}
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                        <button onClick={onClose} className="px-3.5 py-2 text-sm font-semibold rounded-lg border border-slate-200 bg-white hover:bg-slate-50">Cancel</button>
                        <button onClick={() => onConfirm(name)} disabled={busy || !name.trim()}
                            className="px-3.5 py-2 text-sm font-semibold rounded-lg bg-[var(--fw-accent)] hover:bg-[var(--fw-accent-hover)] text-white disabled:opacity-60"
                            data-testid="add-station-confirm">
                            {busy ? "Adding…" : "Add station"}
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function tierLabel(t) {
    if (t === "pro_plus") return "WeatherLink Pro+";
    if (t === "pro") return "WeatherLink Pro";
    return "WeatherLink Basic";
}

function TierBadge({ tier }) {
    const map = {
        pro_plus: { text: "Pro+", cls: "bg-violet-50 text-violet-700 border-violet-200" },
        pro: { text: "Pro", cls: "bg-sky-50 text-sky-700 border-sky-200" },
        basic: { text: "Basic", cls: "bg-slate-100 text-slate-600 border-slate-200" },
    };
    const m = map[tier] || map.basic;
    return <span className={cn("text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md border", m.cls)}>{m.text}</span>;
}

function _slugify(name) {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
}

// ---------- Historical import panel ----------------------------------------
const METRIC_CHOICES = [
    { key: "temperature", label: "Temperature" },
    { key: "humidity", label: "Humidity" },
    { key: "wind", label: "Wind" },
    { key: "rainfall", label: "Rainfall" },
    { key: "solar", label: "Solar radiation" },
    { key: "pressure", label: "Pressure" },
    { key: "dew_point", label: "Dew point" },
    { key: "et", label: "Evapotranspiration" },
];

function HistoricalPanel() {
    const { data: farm } = useQuery({ queryKey: ["farm"], queryFn: fetchFarm });
    const qc = useQueryClient();
    const { data: jobs } = useQuery({
        queryKey: ["import-jobs"], queryFn: listImports, refetchInterval: 2000,
    });
    const [stationId, setStationId] = useState("");
    const [dateFrom, setDateFrom] = useState(defaultFrom());
    const [dateTo, setDateTo] = useState(today());
    const [metrics, setMetrics] = useState(METRIC_CHOICES.map((m) => m.key));

    useEffect(() => {
        if (!stationId && farm?.stations?.length) setStationId(farm.stations[0].id);
    }, [farm, stationId]);

    const selected = farm?.stations?.find((s) => s.id === stationId);
    const noHistorical = selected?.tier === "basic";

    const startMut = useMutation({
        mutationFn: startImport,
        onSuccess: () => {
            toast.success("Import started — dashboard will refresh when complete");
            qc.invalidateQueries({ queryKey: ["import-jobs"] });
            // Auto-refresh farm/frost/YTD data after imports finish (job polling handles the trigger).
            setTimeout(() => qc.invalidateQueries(), 5000);
        },
        onError: (e) => toast.error(e?.response?.data?.detail || "Failed to start import"),
    });

    if (!farm) return <div className="h-40 bg-white rounded-2xl border animate-pulse" />;

    return (
        <div className="space-y-5">
            <div className="fw-card p-5">
                <h2 className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: "Outfit" }}>Import historical WeatherLink data</h2>
                <p className="text-xs text-slate-500 mb-4">
                    WeatherLink v2 caps a single historic-data request at 24 hours; longer ranges are automatically split into sequential 24h chunks and combined into the local database.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <label className="block">
                        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Station</span>
                        <select value={stationId} onChange={(e) => setStationId(e.target.value)}
                            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 font-medium"
                            data-testid="import-station">
                            {farm.stations.map((s) => (
                                <option key={s.id} value={s.id}>{s.name} · {tierLabel(s.tier)}</option>
                            ))}
                        </select>
                    </label>
                    <label className="block">
                        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                            <CalendarDays className="h-3 w-3" /> From
                        </span>
                        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 fw-metric font-medium"
                            data-testid="import-from" />
                    </label>
                    <label className="block">
                        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                            <CalendarDays className="h-3 w-3" /> To
                        </span>
                        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 fw-metric font-medium"
                            data-testid="import-to" />
                    </label>
                </div>

                <div className="mt-4">
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Metrics</div>
                    <div className="flex flex-wrap gap-2">
                        {METRIC_CHOICES.map((m) => {
                            const on = metrics.includes(m.key);
                            return (
                                <button key={m.key}
                                    onClick={() => setMetrics((mm) => on ? mm.filter((k) => k !== m.key) : [...mm, m.key])}
                                    data-testid={`import-metric-${m.key}`}
                                    className={cn("px-3 py-1.5 rounded-full border text-xs font-semibold transition",
                                        on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50")}>
                                    {m.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {noHistorical ? (
                    <div className="mt-4 flex items-start gap-3 p-3 rounded-xl border" style={{ background: "#FFEDD5", borderColor: "#FDBA74" }}>
                        <AlertTriangle className="h-4 w-4 text-orange-600 mt-0.5" />
                        <div className="text-sm">
                            <div className="font-semibold text-orange-800">Historical data requires WeatherLink Pro or Pro+</div>
                            <div className="text-orange-700 text-xs">This station's WeatherLink plan is <strong>Basic</strong>. Upgrade the plan on the Davis dashboard, or add a Pro/Pro+ station to enable imports.</div>
                        </div>
                    </div>
                ) : (
                    <div className="mt-4 flex justify-end">
                        <button
                            onClick={() => startMut.mutate({ station_id: stationId, date_from: dateFrom, date_to: dateTo, metrics })}
                            disabled={startMut.isPending || metrics.length === 0}
                            data-testid="import-start"
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-[var(--fw-accent)] hover:bg-[var(--fw-accent-hover)] text-white disabled:opacity-60">
                            <Download className="h-4 w-4" /> Start import
                        </button>
                    </div>
                )}
            </div>

            <div className="fw-card p-5">
                <h2 className="text-lg font-semibold text-slate-900 mb-3" style={{ fontFamily: "Outfit" }}>Import history</h2>
                {(!jobs || jobs.length === 0) && <div className="text-sm text-slate-500 py-4">No imports yet.</div>}
                <div className="space-y-2">
                    {jobs?.map((j) => (
                        <ImportRow key={j.id} job={j} />
                    ))}
                </div>
            </div>
        </div>
    );
}

function ImportRow({ job }) {
    const done = job.status === "complete";
    const failed = job.status === "failed";
    return (
        <div className="p-3 rounded-xl border border-slate-200" data-testid={`import-row-${job.id}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <div className="font-semibold text-slate-900">{job.station_name}</div>
                    <div className="text-[11px] text-slate-500 fw-metric">
                        {job.date_from} → {job.date_to} · {job.metrics.length} metrics
                    </div>
                </div>
                <span className={cn("text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md border",
                    done ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : failed ? "bg-red-50 text-red-700 border-red-200"
                            : "bg-sky-50 text-sky-700 border-sky-200")}>
                    {job.status}
                </span>
            </div>
            <div className="mt-2 h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                <div className={cn("h-full rounded-full transition-all", done ? "bg-emerald-500" : failed ? "bg-red-500" : "bg-sky-500")}
                    style={{ width: `${job.progress}%` }} />
            </div>
            <div className="flex items-center justify-between mt-1.5 text-[11px] text-slate-500 fw-metric">
                <span>{job.completed_days}/{job.total_days} day{job.total_days > 1 ? "s" : ""}{job.current_chunk ? ` · at ${job.current_chunk}` : ""}</span>
                <span>{job.progress}%</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 text-[11px]">
                <ImportStat label="Observations" value={job.records_imported?.toLocaleString() ?? "0"} />
                <ImportStat label="Rain records" value={(job.rainfall_records ?? 0).toLocaleString()} />
                <ImportStat label="Frost events" value={job.frost_events_calculated ?? "—"} />
                <ImportStat label="Unavailable"
                    value={job.unavailable_metrics?.length ? job.unavailable_metrics.join(", ") : "none"} />
            </div>
            {job.errors?.length > 0 && (
                <div className="mt-2 text-xs text-red-600 space-y-0.5">
                    {job.errors.map((e, i) => <div key={i}>· {e}</div>)}
                </div>
            )}
        </div>
    );
}

function ImportStat({ label, value }) {
    return (
        <div className="rounded-md bg-slate-50 border border-slate-100 px-2 py-1.5">
            <div className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">{label}</div>
            <div className="fw-metric font-semibold text-slate-800 truncate">{value}</div>
        </div>
    );
}

function today() {
    return new Date().toISOString().slice(0, 10);
}
function defaultFrom() {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().slice(0, 10);
}
